import type { ReactElement, SVGProps } from 'react';

import styles from './Icon.module.css';
import type { IconName } from './iconName';

/**
 * The icon sprite — docs/DESIGN.md §9 ("all iconography is inline SVG, stroke 1.5–1.6"),
 * docs/ARCHITECTURE.md §8.2.
 *
 * ONE union, not two. `IconName` is declared in `./iconName.ts` and only re-exported here:
 * `src/features/types.ts` references it from inside `tsconfig.core.json`'s DOM-free, JSX-free
 * program, so the union may not live in a `.tsx`. Adding a glyph is one entry in that file
 * and one entry in `GLYPHS` below — nothing else.
 *
 * The paths are inlined rather than served through `<symbol>` + `<use href="#…">`. A
 * `<use>`-based sprite needs exactly one copy of the sprite mounted somewhere in the document
 * before any icon paints; getting that wrong renders blank squares, and the failure is
 * invisible in unit tests because jsdom does not resolve `<use>` at all. Twenty-odd glyphs of
 * path data cost less than that class of bug.
 *
 * Every icon is `aria-hidden`. docs/DESIGN.md §7.5: no meaning is carried by an icon alone —
 * severity is shape + word + tone, and every icon-only control carries an `aria-label`.
 */

export type { IconName };

const GLYPHS: Record<IconName, ReactElement> = {
  // ── commands ──────────────────────────────────────────────────────────────────────────
  /** Format: four lines of differing length — a document given shape. */
  code: <path d="M2.2 3.4h11.6M5.4 6.6h8.4M5.4 9.8h5.6M2.2 13h11.6" />,
  /**
   * Minify: →|← — two arrows pressing a line together from either side. ADR-0101 §7.17
   * replaced the vertical squeeze, whose six strokes met in the middle and read as an asterisk
   * at 16px.
   */
  minify: <path d="M2 8h4M4 6l2 2-2 2M14 8h-4M12 6l-2 2 2 2M8 3.33v9.34" />,
  copy: (
    <>
      <rect x="5.6" y="2.2" width="8.2" height="8.2" rx="1.4" />
      <path d="M10.4 12.6v.6a1.4 1.4 0 0 1-1.4 1.4H3.6a1.4 1.4 0 0 1-1.4-1.4V7.4A1.4 1.4 0 0 1 3.6 6h.6" />
    </>
  ),
  paste: (
    <>
      <path d="M6 2.8H4.6a1.4 1.4 0 0 0-1.4 1.4v8.6a1.4 1.4 0 0 0 1.4 1.4h6.8a1.4 1.4 0 0 0 1.4-1.4V4.2a1.4 1.4 0 0 0-1.4-1.4H10" />
      <rect x="6" y="1.4" width="4" height="2.8" rx=".9" />
    </>
  ),
  clear: (
    <path d="M2.6 4.2h10.8M6.2 4.2V2.9a.9.9 0 0 1 .9-.9h1.8a.9.9 0 0 1 .9.9v1.3M4.2 4.2l.6 8.5a1.2 1.2 0 0 0 1.2 1.1h4a1.2 1.2 0 0 0 1.2-1.1l.6-8.5" />
  ),
  /** Examples: a file with braces on it — sample DOCUMENTS, not a book (ADR-0101 §7.17). */
  examples: (
    <path d="M9.33 2H4.67a1.33 1.33 0 0 0-1.34 1.33v9.34A1.33 1.33 0 0 0 4.67 14h6.66a1.33 1.33 0 0 0 1.34-1.33V5.33zM9.33 2v3.33h3.34M7 8 6 9.17l1 1.16M9 8l1 1.17-1 1.16" />
  ),
  /**
   * Repair: a large and a small four-pointed spark — "the tool fixed this", the glyph of the
   * lemon family (Fix all, Fix, the repair banner and log). It replaced `wand`, which read as
   * a pencil at toolbar size and so promised an edit mode that does not exist.
   */
  spark: (
    <path d="M7.33 2.33l1.27 3.4 3.4 1.27-3.4 1.27-1.27 3.4-1.26-3.4L2.67 7l3.4-1.27zM12.33 10.33l.47 1.2 1.2.47-1.2.47-.47 1.2-.47-1.2-1.2-.47 1.2-.47z" />
  ),
  /** Undo: a hooked arrow back — the repair banner's Undo and the log's "Undo all". */
  undo: <path d="M6 9.33 3 6.33l3-3M3 6.33h7a3.33 3.33 0 0 1 0 6.67H8" />,
  'arrow-down': <path d="M8 2.6v10.8M4.2 9.6 8 13.4l3.8-3.8" />,
  'arrow-up': <path d="M8 13.4V2.6M4.2 6.4 8 2.6l3.8 3.8" />,

  // ── views ─────────────────────────────────────────────────────────────────────────────
  editor: <path d="m5.4 4.4-3.6 3.6 3.6 3.6M10.6 4.4 14.2 8l-3.6 3.6" />,

  // ── chrome. The four severity glyphs differ by SHAPE, so a greyscale screenshot keeps
  //    every bit of the information (docs/DESIGN.md §1.7, §6.6).
  /** success — filled-feeling ring with a tick */
  check: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="m5.4 8.2 1.9 1.9 3.4-4" />
    </>
  ),
  /** warning — triangle */
  warning: (
    <>
      <path d="M8 2.2 14.6 13.4H1.4Z" />
      <path d="M8 6.6v3" />
      <circle cx="8" cy="11.5" r=".75" fill="currentColor" stroke="none" />
    </>
  ),
  /** error — ring with a bang */
  error: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.8v4" />
      <circle cx="8" cy="11.1" r=".75" fill="currentColor" stroke="none" />
    </>
  ),
  /** note — ring with an i */
  info: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 7.4v3.6" />
      <circle cx="8" cy="5.2" r=".75" fill="currentColor" stroke="none" />
    </>
  ),
  close: <path d="m4.2 4.2 7.6 7.6M11.8 4.2l-7.6 7.6" />,
  /** The header's privacy note: nothing you paste leaves this tab. Lime, like "valid". */
  lock: (
    <>
      <rect x="3.33" y="7" width="9.33" height="6.67" rx="1.67" />
      <path d="M5.67 7V5a2.33 2.33 0 0 1 4.67 0v2" />
    </>
  ),
  'chevron-down': <path d="m4 6.5 4 4 4-4" />,
  sun: (
    <>
      <circle cx="8" cy="8" r="3.1" />
      <path d="M8 1.4v1.7M8 12.9v1.7M14.6 8h-1.7M3.1 8H1.4M12.7 3.3l-1.2 1.2M4.5 11.5l-1.2 1.2M12.7 12.7l-1.2-1.2M4.5 4.5 3.3 3.3" />
    </>
  ),
  moon: <path d="M13.2 9.6A5.6 5.6 0 0 1 6.4 2.8a5.6 5.6 0 1 0 6.8 6.8Z" />,
  monitor: (
    <>
      <rect x="1.8" y="2.8" width="12.4" height="8.4" rx="1.3" />
      <path d="M5.8 14.2h4.4M8 11.2v3" />
    </>
  ),
  globe: (
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M1.9 8h12.2" />
      <path d="M8 1.8a9.4 9.4 0 0 1 0 12.4 9.4 9.4 0 0 1 0-12.4Z" />
    </>
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'name'> {
  readonly name: IconName;
  /**
   * Rendered as width/height attributes. CSS wins over presentation attributes, so a button
   * that wants 15px says so in its module and this stays the sane default.
   */
  readonly size?: number;
}

export function Icon({ name, size = 16, className, ...rest }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className === undefined ? styles.icon : `${styles.icon} ${className}`}
      {...rest}
    >
      {GLYPHS[name]}
    </svg>
  );
}

export default Icon;
